"use client";

import { useEffect, useState } from "react";
import CommonBanner from "../commonBanner/CommonBanner";
import Breadcrumbs from "../breadCrumbAndButton/BreadCrumbAndButton";
import SideBar, { SideBarTopCard } from "../sideBar/SideBar";
import "./style.css";
import ScamNews from "@/components/scamNews/ScamNews";
import "@/components/scamNews/style.css";
import { Button } from "react-bootstrap";
import Link from "next/link";
import { Post, SidebarApiResponse } from "@/types/post.types";
import ExchangeAndWalllets from "@/components/exchangeAndWallets/ExchangeAndWallets";
import Subscribe from "../subscribe/subscribe";
import FeaturedSliderSmall from "@/components/featuredSlider/FeaturedSliderSmall";
import NewsLetter from "@/components/newsletter/NewsLetter";
import CommonCard from "@/components/commonCard/CommonCard";
import PostSkeleton from "@/components/skeletons/PostSkeleton";
import JsonLd from "@/components/jsonLd/JsonLd";
import AdSpace from "@/components/ads/Adspace";
import type { AdItem } from "@/lib/getAdSpaceData";
import type { SidebarData } from "@/lib/getSidebarData";

interface SsrData {
  featuredPosts: Post[];
  sliderPosts: Post[];
  breakingNews: Post[];
  ai: Post[];
  latestPosts: Post[];
  scamsNews: Post[];
}

interface Props {
  newsDetail: any;
  articleSchema: any;
  /** When passed from the server page, client-side fetch is skipped entirely (SSR) */
  ssrData?: SsrData;
  initialAds: AdItem[];
  sidebarData?: SidebarData;
}

const ensureAbsolute = (url?: string) => {
  if (!url || url === "/" || url === "location not found") return "/";
  if (url.startsWith("/") || url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `/${url}`;
};

export default function NewsArchiveDetail({
  newsDetail,
  articleSchema,
  ssrData,
  initialAds,
  sidebarData,
}: Props) {
  // If ssrData is provided we're already hydrated — no loading needed
  const [loading, setLoading] = useState(!ssrData);
  const [featuredPosts, setFeaturedPosts] = useState<Post[]>(
    ssrData?.featuredPosts ?? [],
  );
  const [sliderPosts, setSliderPosts] = useState<Post[]>(
    ssrData?.sliderPosts ?? [],
  );
  const [breakingNews, setBreakingNews] = useState<Post[]>(
    ssrData?.breakingNews ?? [],
  );
  const [AiNews, setAiNews] = useState<Post[]>(ssrData?.ai ?? []);
  const [latestPosts, setLatestPosts] = useState<Post[]>(
    ssrData?.latestPosts ?? [],
  );
  const [scamsNews, setScamsNews] = useState<Post[]>(ssrData?.scamsNews ?? []);
  const [hasAd, setHasAd] = useState(false);

  useEffect(() => {
    // Skip fetch entirely when SSR data is already provided
    if (ssrData) return;

    const fetchData = async () => {
      try {
        const res = await fetch("/api/homepage-data");
        if (!res.ok) throw new Error("Failed to fetch sidebar data");

        const data: SidebarApiResponse = await res.json();
        setSliderPosts(data?.sliderPosts || []);
        setFeaturedPosts(data?.featuredPosts || []);
        setBreakingNews(data?.breakingNews || []);
        setAiNews(data?.ai || []);
        setLatestPosts(data?.latestPosts || []);
        setScamsNews(data?.scamsNews || []);
      } catch (error) {
        console.error("Sidebar fetch error:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [ssrData]);

  if (!newsDetail?.category_slug) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center text-center h-100 py-5">
        <h2 className="pt-5 pb-4">This page is under construction!</h2>
        <Link href={"/"}>
          <Button
            className="bg-info"
            style={{ padding: "10px 15px", color: "white" }}
          >
            Go Home!
          </Button>
        </Link>
      </div>
    );
  }

  const picks = [
    {
      title: "Bitget",
      imageSrc: "/exchange-and-wallet/Bitget-Logo-1.png",
      link: "#",
    },
    {
      title: "MEXC",
      imageSrc: "/exchange-and-wallet/MEXC-Logo.jpg",
      link: "#",
    },
    {
      title: "ByBit",
      imageSrc: "/exchange-and-wallet/unnamed-2.png",
      link: "#",
    },
    {
      title: "Trezor",
      imageSrc: "/exchange-and-wallet/trezor-square.jpg.webp",
      link: "#",
    },
    {
      title: "OKX",
      imageSrc: "/exchange-and-wallet/okx-logo-black-and-white.png",
      link: "#",
    },
  ];

  const formatDate = (dateString?: string) => {
    if (!dateString) return "";
    const date = new Date(dateString);
    const datePart = date.toLocaleDateString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
    });
    const timePart = date.toLocaleTimeString("en-US", {
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    });
    return `${datePart} • ${timePart}`;
  };

  return (
    <>
      {articleSchema && <JsonLd data={articleSchema} />}
      <div className="">
        <div className="post-head-wrapper-banner rounded-0">
          <picture>
            <source
              srcSet={
                newsDetail?.image_big
                  ? `${process.env.NEXT_PUBLIC_API_BASE_URL}/${newsDetail?.image_big}`
                  : newsDetail?.image_url || "/image-not-found.png"
              }
              sizes="(max-width: 1680px) 95vw, (min-width: 1681px) 1600px"
              type="image/webp"
            />
            <div className="post-breadcrumb-overlay px-md-5 mx-0 d-md-none">
              <Breadcrumbs
                items={[
                  { label: "Home", href: "/" },
                  {
                    label: `${newsDetail?.category_name}`,
                    href: newsDetail?.category_slug,
                  },
                  { label: `${newsDetail?.title}` },
                ]}
              />
            </div>
            <img
              srcSet={newsDetail?.featureImageSrcSet}
              sizes="(max-width: 1680px) 95vw, (min-width: 1681px) 1600px"
              src={
                newsDetail?.featureImage ||
                newsDetail?.image_url ||
                "/image-not-found.png"
              }
              alt={newsDetail?.title}
              className="post-feature-image-banner"
            />
          </picture>

          <CommonBanner
            authorSlug={newsDetail?.author_slug}
            authorImageSrcSetWebp={
              newsDetail?.author_avatar || "/assets/img/user.png"
            }
            authorImageSrcSet={newsDetail?.author_avatar}
            authorImage={newsDetail?.author_avatar}
            authorName={newsDetail?.author_username}
            dateLabel={newsDetail?.created_at}
            readTime={newsDetail?.readTime}
            title={newsDetail?.title}
            excerpt={newsDetail?.summary}
            facebookUrl={newsDetail?.category_name}
            twitterUrl={newsDetail?.category_name}
            title_slug={newsDetail?.title_slug}
          />
        </div>
      </div>

      <div className="gh-container post-main-wrapper with-sidebar">
        <div className="row gap-2">
          {/* Main Content Column */}
          <div className="col-lg-8 col-md-7 post-page-main">
            <div className="post-content with-sidebar px-2">
              {/* <h2 id="vision" className="px-0 mx-0">
                {newsDetail?.category_name}
              </h2> */}
              {/* <p>{newsDetail?.summary}</p> */}
              <div dangerouslySetInnerHTML={{ __html: newsDetail?.content }} />
            </div>

            <div className="post-page-bottom-content with-sidebar px-2 d-flex justify-content-between align-items-center flex-column flex-md-row gap-3">
              <p className="mb-0">
                By <strong>{newsDetail?.author_username}</strong> • Updated on{" "}
                {formatDate(newsDetail?.created_at)}
              </p>
              {newsDetail?.category_name && (
                <Link href={ensureAbsolute(newsDetail?.category_slug)}>
                  <span
                    className="badge gh-tag"
                    style={
                      {
                        "--accent": newsDetail?.category_color ?? "#00897B",
                        background: newsDetail?.category_color
                          ? `color-mix(in srgb, ${newsDetail.category_color} 80%, transparent)`
                          : "#00897B",
                        color: "#ffffff",
                      } as React.CSSProperties
                    }
                  >
                    {newsDetail?.category_name}
                  </span>
                </Link>
              )}
            </div>
            <hr />
            <h2 className="fs-1 mt-4 mb-3">Read Next</h2>
            <div className="row g-4 mt-3">
              {loading
                ? Array.from({ length: 4 }).map((_, i) => (
                  <div className="col-12 col-md-6" key={i}>
                    <PostSkeleton />
                  </div>
                ))
                : latestPosts.slice(0, 4).map((item) => (
                  <div
                    className="col-12 col-md-6 card-container"
                    key={item.id}
                  >
                    <CommonCard
                      bodyClassName="px-0"
                      titleClassName="section-card"
                      authorName={item.author}
                      publishDate={item.date}
                      titleText={item.title}
                      category_name={item.category_name || ""}
                      category_slug={item.category_slug || ""}
                      category_color={item.category_color || ""}
                      sub_category_name={item.sub_category_name || ""}
                      sub_category_slug={item.sub_category_slug || ""}
                      sub_category_color={item.sub_category_color || ""}
                      imgSrc={
                        item.image
                          ? item.image.replace(/([^:]\/)\/+/g, "$1")
                          : "/image-not-found.png"
                      }
                      cardLink={`/${item.url}`}
                      authorSlug={`${item.author_slug}` || ""}
                      authorSlugClassName="authors-slug"
                    />
                  </div>
                ))}
            </div>
          </div>

          {/* Sidebar Column */}
          <div className="col-lg-4 col-md-5 mx-md-3 py-5">
            {loading ? (
              <div className="placeholder-glow">
                <div
                  className="placeholder col-12 mb-3"
                  style={{ height: "200px" }}
                ></div>
                <div
                  className="placeholder col-12"
                  style={{ height: "300px" }}
                ></div>
              </div>
            ) : (
              <>
                <div className="mb-4 mx-0">
                  <AdSpace
                    slot="sidebar_1"
                    viewport="auto"
                    initialAds={initialAds}
                    onRender={(rendered) => setHasAd(rendered)}
                  />
                </div>
                <SideBarTopCard />
                <hr />
                <FeaturedSliderSmall featuredPosts={featuredPosts} />
                <SideBar initialData={sidebarData} />
                <div className="overflow-visible px-2">
                  <ScamNews news={scamsNews} />
                </div>
                <ExchangeAndWalllets picks={picks} />
                <Subscribe />
                <div className="mt-4">
                  <AdSpace
                    slot="sidebar_2"
                    viewport="auto"
                    initialAds={initialAds}
                    onRender={(rendered) => setHasAd(rendered)}
                  />
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="mb-4">
        <AdSpace
          slot="posts_bottom"
          viewport="auto"
          initialAds={initialAds}
          onRender={(rendered) => setHasAd(rendered)}
        />
      </div>

      <NewsLetter />
    </>
  );
}
